import React, { memo, useState, useEffect, useRef, useMemo } from 'react';
import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { debounce } from 'lodash';

import { Spinner } from 'components';
import SelectionTickMark from 'components/form/SelectionTickMark';
import { useResponsive } from 'hooks/useResponsive';
import { useNotification } from 'hooks/useNotification';
import { useCandidate } from 'hooks/useCandidate';
import useScrollOffset from 'hooks/useScrollOffset';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { useCandidateOnboarding } from 'hooks/useCandidateOnboarding';
import { Option } from 'lib/models/option';
import { MATCHING_PREFERENCE_STEPS } from 'lib/models/candidate';
import { sortedCountriesList } from 'utils/format';
import { Footer } from '../Footer/Footer.component';
import MobileHeaderMp from '../MobileHeaderMp';
import { SidebarMP } from '../SidebarMP/SidebarMP.component';

import styles from '../Onboarding.module.scss';

export interface VisaPreferenceProps {
  /** Custom class name */
  className?: string;
  candidateChoice: Array<string>;
  candidateLocationCountries: Array<{ id: string; name: string }>;
  visaCountries: Array<Option> | undefined;
  allCountries: Array<Option> | undefined;
  onNextClick: (
    visa_country_ids: Array<string>,
    isBackClicked?: boolean
  ) => void;
  isLoading: boolean;
  isBackLoading: boolean;
  onStepSelect?: (step: MATCHING_PREFERENCE_STEPS) => void;
}

export const VisaPreference: React.FC<VisaPreferenceProps> = memo(
  ({
    className = '', // custom class name
    candidateChoice,
    candidateLocationCountries,
    visaCountries,
    allCountries,
    onNextClick,
    isLoading,
    isBackLoading,
    onStepSelect,
  }: VisaPreferenceProps) => {
    const [t] = useTranslation('candidate-onboarding');
    const { isWindowScrolled } = useScrollOffset();
    const bottomRef = useRef<HTMLDivElement>(null);
    const screens = useResponsive();
    const notificationInstance = useNotification();
    const { updateCandidateInfo } = useCandidate();
    const { isValueEdited, setValueEdited, onBackClickMPMobile } =
      useCandidateOnboarding();

    const contentClassNames = cn(styles.container, className);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const [fullyScrolled, setFullyScrolled] = useState(false);

    const [selectedVisaPreference, setSelectedVisaPreference] = useState<
      Array<string>
    >([]);

    useEffect(() => {
      if (candidateChoice.length) {
        let ids: Array<string> = [];
        candidateLocationCountries.map(({ id }) => {
          if (candidateChoice.includes(id)) {
            ids = [...ids, id];
          }
        });
        setSelectedVisaPreference(ids);
      }
    }, [candidateChoice]);

    useVisibleOnIntersect({
      elRef: bottomRef,
      threshold: 0,
      onIntersect: () => setFullyScrolled(true),
      onHidden: () => setFullyScrolled(false),
      rootMargin: '0px',
    });
    const onChevronClick = () => {
      if (isValueEdited) {
        onBackClickMPMobile();
      } else {
        onSubmit(false);
      }
    };
    const showCountriesList =
      candidateLocationCountries && candidateLocationCountries?.length !== 1;

    const apiCallToUpdate = async (modifiedSelection: Array<string>) => {
      try {
        await updateCandidateInfo({
          partialCandidateAttributes: {
            visa_country_ids: modifiedSelection,
          },
        });
      } catch (error) {
        notificationInstance.handleExceptionError(error);
      }
    };

    const debouncedAPICall = React.useRef(
      debounce(async (modifiedSelection) => {
        await apiCallToUpdate(modifiedSelection);
      }, 3000)
    ).current;

    const onCountrySelect = (id?: string) => {
      setValueEdited(true);

      const index = selectedVisaPreference.findIndex((item) => item === id);
      let modifiedSelection: Array<string> = [];

      if (id) {
        if (index === -1 || index === undefined) {
          if (selectedVisaPreference.length === 0) modifiedSelection = [id];
          else modifiedSelection = [...selectedVisaPreference, id];
        } else {
          modifiedSelection = selectedVisaPreference.filter(
            (item) => item !== id
          );
        }
      }
      /** For MP
       * Desktop: Autosave on every change
       *          Do not make changes when nothing is selected on mandatory step
       * Mobile: Save on CTA click
       */
      setSelectedVisaPreference(modifiedSelection);

      /** Add isMatchingPreference condition if this component will be used in any other flow */
      if (!screens.sm) {
        debouncedAPICall(modifiedSelection);
      }
    };

    const onSubmit = (isBackClicked?: boolean) => {
      setValueEdited(false);
      onNextClick(selectedVisaPreference, isBackClicked);
    };

    if (!visaCountries || !allCountries) {
      return (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      );
    }

    const countriesList = useMemo(() => {
      if (!visaCountries || !allCountries) return;
      const filteredCountries = allCountries.filter(({ id }) =>
        visaCountries.find((item) => item.id === id)
      );
      return sortedCountriesList(filteredCountries);
    }, [visaCountries, allCountries]);

    return (
      <div>
        <MobileHeaderMp
          title={t('matching-preferences.visa-sponsorship')}
          onChevronClick={onChevronClick}
        />
        <div
          className={contentClassNames}
          data-testid="VisaPreference"
          ref={containerRef}
        >
          {onStepSelect && (
            <SidebarMP className={styles.sidebar} onStepSelect={onStepSelect} />
          )}

          <div className={cn({ [styles.content]: true }, styles['content-mp'])}>
            {showCountriesList ? (
              <>
                <p className={styles.setup}>{t('visa-preference.title')}</p>
                <p className={styles.desc}>{t('visa-preference.subtitle')}</p>
              </>
            ) : (
              <p className={styles.setup}>
                {t('visa-preference.single-country-visa', {
                  country: candidateLocationCountries[0].name,
                })}
              </p>
            )}
            {showCountriesList ? (
              <div className={styles.gap}>
                {countriesList?.map((item) => {
                  return (
                    <SelectionTickMark
                      key={item?.id}
                      checked={selectedVisaPreference.some(
                        (id) => item?.id === id
                      )}
                      className={cn(styles.radio)}
                      isTickSquare={true}
                      onChange={() => onCountrySelect(item!.id)}
                    >
                      {item?.label}
                    </SelectionTickMark>
                  );
                })}

                <SelectionTickMark
                  className={cn(styles.radio, styles.gap)}
                  showSelectionCircle
                  checked={selectedVisaPreference.length === 0}
                  onChange={() => onCountrySelect()}
                >
                  {t('visa-preference.no-visa-requirement')}
                </SelectionTickMark>
              </div>
            ) : (
              <div className={styles.gap}>
                <SelectionTickMark
                  className={styles.radio}
                  showSelectionCircle
                  checked={selectedVisaPreference.length > 0}
                  onChange={() =>
                    onCountrySelect(candidateLocationCountries[0].id)
                  }
                >
                  {t('visa-preference.yes-visa-requirement')}
                </SelectionTickMark>
                <SelectionTickMark
                  className={styles.radio}
                  showSelectionCircle
                  checked={selectedVisaPreference.length === 0}
                  onChange={() => onCountrySelect()}
                >
                  {t('visa-preference.no-visa-requirement')}
                </SelectionTickMark>
              </div>
            )}
            <div className={styles['bottom-ref']} ref={bottomRef}></div>
          </div>

          <Footer
            isReady={true}
            isBackLoading={isBackLoading}
            isLoading={isLoading}
            onSubmit={onSubmit}
            isFullyScrolled={fullyScrolled}
            isWindowScrolled={isWindowScrolled}
            isMatchingPreference={true}
          />
        </div>
      </div>
    );
  }
);

VisaPreference.displayName = 'VisaPreference';
